import camelcaseKeys from 'camelcase-keys'
import throttle from 'lodash.throttle'

import { NotFoundError } from '@/modules/errors'
import { executeGet, executePut } from '@/services/lukita'
import type {
  ProjectActions,
  ProjectLoaders,
  ProjectMutations,
  ProjectStoreCreator,
} from './types'

export const projectActions: ProjectStoreCreator<ProjectActions> = (
  set,
  _get,
) => ({
  startLoadingProject: () => set({ loadingProject: true }),
  stopLoadingProject: () => set({ loadingProject: false }),

  updateProject: (attrs) => {
    set((state) => {
      if (!state.project) return state
      return { project: { ...state.project, ...camelcaseKeys(attrs) } }
    })
  },

  setProject: (project) =>
    set({ project: project ? camelcaseKeys(project) : project }),
})

export const projectMutations: ProjectStoreCreator<ProjectMutations> = (
  set,
  get,
) => {
  let lastCaseSave: Promise<unknown> = Promise.resolve()

  return {
    // Two quick edits each send the whole answers hash. Run side by side, the second would be
    // built before the first landed and would undo it.
    queueCaseSave: (save) => {
      const thisSave = lastCaseSave
        .catch(() => undefined)
        .then(async () => {
          const project = get().project
          if (project) await save(project)
        })
      lastCaseSave = thisSave
      return thisSave
    },

    updateProject: async (body) => {
      set({ mutating: true })
      const project = get().project
      if (!project) return

      const path = `/projects/${project.slug}`
      const response = await executePut(path, body).finally(() =>
        set({ mutating: false }),
      )

      const updatedProject = await response.json()
      get().actions.setProject(updatedProject)
      return updatedProject
    },
  }
}

export const projectLoaders: ProjectStoreCreator<ProjectLoaders> = (
  _set,
  get,
) => ({
  fetchProject: throttle(async (slug) => {
    const currentProject = get().project
    if (!currentProject || currentProject.slug !== slug) {
      get().actions.startLoadingProject()
    }

    const response = await executeGet(`/projects/${slug}`)
    const project = await response.json()
    if (!project) throw new NotFoundError()

    get().actions.setProject(project)
    get().actions.stopLoadingProject()
  }, 500),
})
